import React, { useEffect, useState } from "react";
import { useHeaderContext } from "../../../../context/headerContext";
import { useCartContext } from "../../../../context/cartContext";
import { useDrawerContext } from "../../../../context/drawerContext";
import { useNavigate } from "react-router";
import { useUserContext } from "../../../../context/userContext";
import Button from '../../../../components/Button'
import TextInput from '../../../../components/TextInput'
import CloseIcon from "/public/images/icons/close.svg?react";
import SelectInput from "../../../../components/SelectInput";
import { z } from 'zod'
import QuantityInput from "../../../../components/QuantityInput";
import generateUUID from "../../../../utils/generateUUID";
import fetchWithRefresh from "../../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../../utils/LogErrors";

export default function AddNewParticipant() {
  const { setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { setSelectedParticipant, addProduct, addParticipant } = useCartContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [bandTypes, setBandTypes] = useState([]);
  const { user, logout, updateTokens } = useUserContext();

  useEffect(() => {
    setTitle("ADD NEW PARTICIPANT");
    setBackButton(true);
    setCartButton(true);

    const handleBack = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleBack);
  }, []);

  useEffect(() => {
    fetchAvailableBandTypes();
  }, []);

  const fetchAvailableBandTypes = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Available Band Types", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/FetchBandAvailableTypes`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (response.ok) {
          const data = await response.json();
          setBandTypes(data);
          span.setAttribute("bandTypes.count", data?.length || 0);
        } else {
          throw new Error("Failed to fetch band types");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error fetching band types: " + error);
    }
  };

  const participantSchema = z.object({
    ParticipantId: z.string(),
    ParticipantName: z.string().min(1, "Participant Name is required"),
    IceNumber: z.string().min(1, "ICE Contact telephone number is required"),
    PrimaryMedicalCondition: z.string().min(1, "Primary Medical Condition is required"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.ParticipantId = 'new-participant-'+generateUUID();
  
    const parsedData = participantSchema.safeParse(data);
  
    if (!parsedData.success) {
      const errorMessages = parsedData.error.errors.reduce((acc, error) => {
        acc[error.path[0]] = error.message;
        return acc;
      }, {});
      setErrors(errorMessages);
      LogErrors(user, logout, updateTokens, parsedData.error.errors);
      return;
    }
    setErrors({});
    addParticipant(parsedData.data);
    setSelectedParticipant(parsedData.data.ParticipantId);

    setOpen(true);
    setDrawerContent(<BandSelectionDrawer addProduct={addProduct} setOpen={setOpen} Bands={bandTypes} setDrawerContent={setDrawerContent} />);

  };

  return (
    <div className="p-[20px]">
      <h1 className="text-[24px] leading-[34px] mt-[21px] mb-[24px]">Add New Participant</h1>
      <div>
        <div className='border-b border-dashed mb-[20px] pb-[20px]'>
          <p className='font-semibold'>Participant Details</p>
        </div>

        <form onSubmit={handleSubmit} className='flex flex-col gap-5'>
          <div className='flex flex-col gap-5'>
            <TextInput 
              name="ParticipantName" 
              label="Participant Name" 
              placeholder="Enter full name" 
              error={errors.ParticipantName} 
            />
            <TextInput  
              name="IceNumber" 
              label="ICE Contact Telephone Number" 
              placeholder="Telephone Number" 
              error={errors.IceNumber} 
            />
            <TextInput 
              name="PrimaryMedicalCondition" 
              label="Primary Medical Condition" 
              placeholder="Enter Condition" 
              error={errors.PrimaryMedicalCondition} 
            />
          </div>
          <div className="flex flex-col gap-6 mt-[75px] pb-[50px]">
            <Button variant="primary" submit className="w-full">Create Participant</Button>
            <Button variant="quaternary" className="w-full" onClick={() => navigate("/shop/my-participants")}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

const BandSelectionDrawer = ({ addProduct, setOpen, Bands, setDrawerContent }) => {
  const [BandType, setBandType] = useState("");
  const [BandColour, setBandColour] = useState("");
  const [Quantity, setQuantity] = useState(1);
  const [bandColours, setBandColours] = useState([]);
  const [bandPrice, setBandPrice] = useState(null);

  const { selectedParticipant } = useCartContext();
  const navigate = useNavigate();

  const getBandColours = (bandType, bands) => {
    const band = bands.find(b => b.OrgPartnerFriendlyName === bandType);
    if (band && band.BandColours) {
      return band.BandColours.split('|').map(colour => colour.trim());
    }
    return [];
  };

  useEffect(() => {
    const colours = getBandColours(BandType, Bands);
    setBandColours(colours);

    if (BandType) {
      const selectedBand = Bands.find(band => band.OrgPartnerFriendlyName === BandType);
      if (selectedBand) {
        setBandPrice(selectedBand.OrgPrice);
      }
    }

    if (colours.length === 1) {
      setBandColour(colours[0]);
    }
  }, [BandType, Bands]);

  const createProduct = () => {
    const selectedBand = Bands.find(band => band.OrgPartnerFriendlyName === BandType);

    return {
      ProductId: generateUUID(),
      BandType,
      BandColour,
      Quantity,
      BandImage: selectedBand ? selectedBand.BandImage || selectedBand.Org3dImageURL : '',
      ParticipantId: selectedParticipant,
      Price: selectedBand ? bandPrice : '',
      Org3dImageURL: selectedBand ? selectedBand.Org3dImageURL : '',
      OrgID: selectedBand ? selectedBand.OrgID : '',
      OrgPartnerFriendlyName: selectedBand ? selectedBand.OrgPartnerFriendlyName : '',
      OrgSiteLogo: selectedBand ? selectedBand.OrgSiteLogo : '',
      pricemax: selectedBand ? selectedBand.pricemax : '',
      prmedicalmax: selectedBand ? selectedBand.prmedicalmax : '',
      prnamemax: selectedBand ? selectedBand.prnamemax : ''
    };
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          CHOOSE BAND
        </h2>
        <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <div className="mt-6 flex flex-col gap-2 justify-stretch">
        <h3 className="text-[24px]">Please choose the band type you wish to order</h3>
        <p className="text-sportsDarkGrey mb-[26px]">
          This can be a Sports Guardian branded (with a choice of 5 colours) or a Venue specific branded band.
        </p>
        <div className="flex flex-col gap-4">
          <SelectInput 
            label="Band Type"
            placeholder="Please Select"
            name="BandType"
            className="capitalize"
            options={Bands?.map((band, index) => (
              <option key={index} value={band.OrgPartnerFriendlyName} className="capitalize">
                {band.OrgPartnerFriendlyName}
              </option>
            ))}
            onChange={(e) => setBandType(e.target.value)}
          />
          <div className={`${BandType ? 'block' : 'hidden'}`}>
            <SelectInput 
              label="Band Colour"
              placeholder="Please Select"
              name="BandColour"
              className="capitalize"
              options={bandColours.map((colour, index) => (
                <option key={index} value={colour} className="capitalize">{colour}</option>
              ))}
              onChange={(e) => setBandColour(e.target.value)}
              value={BandColour}
            />
          </div>
          <div className={`${BandType && BandColour ? 'block' : 'hidden'}`}>
            <QuantityInput 
              min={1} 
              label={`Quantity (£${bandPrice} Each)`}
              onChange={(value) => setQuantity(value)}
            />
          </div>
        </div>
        <div className={`${BandType && BandColour && Quantity ? 'block' : 'hidden'}`}>
          <Button 
            variant="primary" 
            className="w-full mt-4"
            onClick={() => {
              const product = createProduct();
              addProduct(product);
              setBandType('');
              setBandColour('');
              setBandColours([]);
              setBandPrice(null);
              setQuantity(1);
              setOpen(false);
              setDrawerContent(<div></div>);
              navigate(`/shop/product-summary?productId=${product.ProductId}&participantId=${selectedParticipant}`, {state:{edit: false}});
            }}
          >
            Confirm & Continue
          </Button>
        </div>
      </div>
    </div>
  );
};